import React, { useState } from 'react';
import { eventsService } from '../../services/eventsService';
import './MovePositionWidget.css';

const DeletePositionWidget = ({ className = '' }) => {
  const [sourcePosition, setSourcePosition] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const source = sourcePosition.trim();
    setMessage('');
    setError('');

    if (!source) {
      setError('Enter the source position to delete.');
      return;
    }

    setSaving(true);
    const result = await eventsService.previewDeletePosition({ source_position: source });
    setSaving(false);

    if (!result.success) {
      setError(result.error);
      return;
    }

    setPreview(result);
  };

  const closePreview = () => {
    if (saving) return;
    setPreview(null);
  };

  const confirmDelete = async () => {
    const source = sourcePosition.trim();
    if (!source) {
      setError('Enter the source position to delete.');
      setPreview(null);
      return;
    }

    setSaving(true);
    setError('');
    setMessage('');
    const result = await eventsService.deletePosition({ source_position: source });
    setSaving(false);

    if (!result.success) {
      setError(result.error);
      setPreview(null);
      return;
    }

    setMessage(
      `${result.message} Units deleted: ${result.units_deleted}. Purchases cancelled: ${result.purchases_cancelled}.`,
    );
    setSourcePosition('');
    setPreview(null);
  };

  const purchaseCount = Number(preview?.purchases_count || 0);
  const purchaseLabel = purchaseCount === 1 ? 'purchase' : 'purchases';

  return (
    <>
      <section className={`move-position-widget delete-position-widget ${className}`.trim()}>
        <div className="move-position-widget-header">
          <h3>Delete Position</h3>
          <p className="move-position-widget-help">
            Permanently delete one position and its units. Enter its source UUID or event ID.
            A confirmation screen will show the affected position before deletion.
          </p>
        </div>

        <form className="move-position-widget-form delete-position-widget-form" onSubmit={handleSubmit}>
          <label>
            <span>Source Position</span>
            <input
              type="text"
              value={sourcePosition}
              onChange={(event) => setSourcePosition(event.target.value)}
              placeholder="e.g. 10887a or event_id"
              disabled={saving}
            />
          </label>

          {error && (
            <div className="move-position-widget-alert move-position-widget-alert-error">
              {error}
            </div>
          )}

          {message && (
            <div className="move-position-widget-alert move-position-widget-alert-success">
              {message}
            </div>
          )}

          <button className="btn delete-position-button move-position-widget-submit" type="submit" disabled={saving}>
            {saving ? 'Checking...' : 'Delete Position'}
          </button>
        </form>
      </section>

      {preview && (
        <div className="move-position-preview-backdrop" onMouseDown={closePreview}>
          <section className="move-position-preview-modal delete-position-preview-modal" onMouseDown={(event) => event.stopPropagation()}>
            <div className="move-position-preview-header">
              <div>
                <h2>Confirm Position Deletion</h2>
                <p>{preview.source_uuid}</p>
              </div>
              <button
                className="move-position-preview-close"
                type="button"
                onClick={closePreview}
                disabled={saving}
                aria-label="Close deletion confirmation"
              >
                x
              </button>
            </div>

            <div className="move-position-preview-grid delete-position-preview-grid">
              <article className="move-position-preview-card delete-position-preview-card">
                <span className="move-position-preview-label">Position to Delete</span>
                <h3>{preview.source_item.event_name || '-'}</h3>
                <p>{preview.source_item.artist || '-'}</p>
                <p>{preview.source_item.event_date || '-'}</p>
                <p>{preview.source_item.venue || '-'}</p>
                <p>{preview.source_item.location || '-'}</p>
                <p>{preview.source_item.username || preview.source_item.assigned_user_raw || '-'}</p>
                <strong>{preview.source_uuid}</strong>
              </article>

              <div className="delete-position-impact">
                <strong>This action cannot be undone.</strong>
                <span>{preview.units_count} units will be permanently deleted.</span>
                {purchaseCount > 0 ? (
                  <span>
                    {purchaseCount === 1
                      ? 'The associated purchase will also be cancelled.'
                      : `${purchaseCount} associated ${purchaseLabel} will also be cancelled.`}
                    {preview.purchase_ids?.length ? ` Purchase IDs: ${preview.purchase_ids.join(', ')}.` : ''}
                  </span>
                ) : (
                  <span>No associated purchase was found.</span>
                )}
              </div>
            </div>

            <div className="move-position-preview-actions">
              <button className="btn btn-secondary" type="button" onClick={closePreview} disabled={saving}>
                Cancel
              </button>
              <button className="btn delete-position-button" type="button" onClick={confirmDelete} disabled={saving}>
                {saving ? 'Deleting...' : 'Confirm Delete'}
              </button>
            </div>
          </section>
        </div>
      )}
    </>
  );
};

export default DeletePositionWidget;
